import { useEffect, useMemo, useState } from 'react';
import { getFantasyLockStatus } from '../services/FantasyService';
import { getPredictionLockStatus } from '../services/MatchService';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** What a lock-status endpoint answers (Fantasy/getFantasyLockStatus, StandingsPrediction/lockStatus). */
export interface LockStatus {
    locked: boolean;
    lockTime: string;
    serverTime: string;
}

export interface DeadlineLock {
    /** False until the status has come back, so callers can avoid flashing "open" at a locked season. */
    loaded: boolean;
    locked: boolean;
    /** The moment the season closes, or null before the status has loaded. */
    lockTime: Date | null;
    /** Milliseconds left until the lock, floored at 0. Null before the status has loaded. */
    msRemaining: number | null;
    /** Under 24 hours left - worth shouting about in the UI. */
    urgent: boolean;
}

/**
 * A season deadline as a live countdown. Fetches the deadline once and ticks every second, so a page left
 * open across the deadline locks itself without a refresh. The remaining time is measured against the
 * server's clock, not the browser's, so a skewed local clock cannot show the wrong deadline (or keep the
 * season looking open after it closed). `fetchStatus` must be a stable (module-level) function.
 */
const useDeadlineLock = (fetchStatus: () => Promise<LockStatus>): DeadlineLock => {
    const [status, setStatus] = useState<{ locked: boolean; lockAt: number; skew: number } | null>(null);
    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        let cancelled = false;
        fetchStatus()
            .then((s) => {
                if (cancelled) return;
                setStatus({
                    locked: s.locked,
                    lockAt: new Date(s.lockTime).getTime(),
                    // Positive when this browser's clock runs ahead of the server's.
                    skew: Date.now() - new Date(s.serverTime).getTime(),
                });
            })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [fetchStatus]);

    useEffect(() => {
        if (!status || status.locked) return;
        let timer: ReturnType<typeof setTimeout>;
        const tick = () => {
            const at = Date.now();
            setNow(at);
            const remaining = status.lockAt - (at - status.skew);
            // Nothing left to count once the deadline has passed.
            if (remaining <= 0) return;
            // Seconds only show in the final hour, so tick lazily until then - these pages render a lot
            // of cards and re-rendering all of them once a second for days is wasted work.
            timer = setTimeout(tick, remaining <= HOUR_MS ? 1000 : 30000);
        };
        timer = setTimeout(tick, 1000);
        return () => clearTimeout(timer);
    }, [status]);

    return useMemo(() => {
        if (!status) {
            return { loaded: false, locked: false, lockTime: null, msRemaining: null, urgent: false };
        }
        const msRemaining = status.lockAt - (now - status.skew);
        return {
            loaded: true,
            locked: status.locked || msRemaining <= 0,
            lockTime: new Date(status.lockAt),
            msRemaining: Math.max(msRemaining, 0),
            urgent: msRemaining > 0 && msRemaining <= DAY_MS,
        };
    }, [status, now]);
};

/**
 * "2d 4t 11m" while there is still a while to go, dropping to "11m 05s" in the final hour where the
 * seconds actually matter. Units are passed in so each page can label them in its own language.
 */
export const formatLockCountdown = (ms: number, units: { d: string; h: string; m: string; s: string }): string => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    if (days > 0) return `${days}${units.d} ${hours}${units.h} ${minutes}${units.m}`;
    if (hours > 0) return `${hours}${units.h} ${minutes}${units.m}`;
    return `${minutes}${units.m} ${String(seconds).padStart(2, '0')}${units.s}`;
};

/** The deadline as a short local date + time, e.g. "17. sep. 23:59". */
export const formatLockDate = (date: Date, locale: string): string =>
    date.toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** The current season's fantasy signup lock. */
export const useFantasyLock = (): DeadlineLock => useDeadlineLock(getFantasyLockStatus);

/** The current season's standings-prediction lock (the same deadline as fantasy, see PowerLigaManager). */
export const usePredictionLock = (): DeadlineLock => useDeadlineLock(getPredictionLockStatus);
